<template>
  <div class="document-card-wrapper">
    <div class="image-container">
      <img
        v-if="isImageValid && firstPageUrl"
        class="document-thumb"
        :src="firstPageUrl"
        alt="thumb"
        @load="handleImageLoad"
        @error="handleImageError"
      />
      <BbIcon v-else-if="!isImageValid && toDownload" name="translate" class="default-thumb" />
      <BbIcon v-else name="slides" class="default-thumb" />
    </div>
    <div class="document-details">
      <h4 :class="['document-title', !isTitleActive ? 'inactive-class' : '']" @click.stop="clickTitle">
        {{ computedTitle }}
      </h4>
      <div>
        <span>{{ data.pageCount }} pages</span>
        <span class="bullet">•</span>
        <span>{{ documentType }}</span>
      </div>
      <slot />
    </div>
    <BbBtn
      v-if="toDownload"
      size="large"
      type="icon"
      icon="download"
      icon-size="small"
      no-background
      icon-type="outlined"
      @click="clickButton"
    ></BbBtn>
    <CollectionButton v-else :document="data" />
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import CollectionButton from '~/components/common/CollectionButton';
import { addLanguageToFileName } from '~/utils/document-utils';
import { isPdfExtension, isTempDocument } from '~/utils/document-utils';
import validateUrl from '~/utils/url';

export default {
  methods: { addLanguageToFileName },
  props: {
    index: {
      type: Number,
      default: 0,
    },
    data: {
      type: Object,
      default: () => {},
    },
    documentType: {
      type: String,
      default: 'PDF',
    },
    toDownload: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CollectionButton,
  },
  setup(props, { emit }) {
    const isImageValid = ref(true);

    const firstPageUrl = computed(() =>
      validateUrl(props.data.pages?.[1]?.small) ? props.data.pages?.[1]?.small : '',
    );
    // consider that every source document (open source) is pdf
    const isTitleActive = computed(() =>
      isTempDocument(props.data.externalId) ? isPdfExtension(props.data.name) : true,
    );

    // need this for old system messages which do not contain translatedFileName
    // will be deleted that part in the future
    const computedTitle = computed(() =>
      props.toDownload
        ? props.data.translatedFileName
          ? props.data.translatedFileName
          : addLanguageToFileName(props.data.name, props.data.targetLanguage)
        : props.index + '. ' + props.data.name,
    );

    const clickTitle = () => emit('title-clicked');
    const clickButton = () => emit('button-clicked');

    const handleImageLoad = () => (isImageValid.value = true);
    const handleImageError = () => (isImageValid.value = false);

    return {
      computedTitle,
      clickButton,
      clickTitle,
      firstPageUrl,
      isImageValid,
      isTitleActive,
      handleImageError,
      handleImageLoad,
    };
  },
};
</script>

<style scoped lang="scss">
.document-card-wrapper {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-2;
  border-bottom: 1px solid $bb-neutral-4;

  .image-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between; /* Aligns children (start, content, end) */
    border-radius: $bb-spacing-1;
    background-color: $bb-neutral-5;
    width: 64px;
    height: 50px;
    align-items: flex-start;
    gap: $bb-spacing-1;

    .default-thumb {
      font-size: $bb-spacing-7;
      align-self: center;
      padding: $bb-spacing-2;
    }

    img {
      max-width: 60px;
      max-height: 48px;
      align-self: center;
    }

    .page-index {
      @include bb-medium-text-styles('sm');
      color: $bb-neutral-1;
    }
  }

  .document-details {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    max-width: 100%;

    .document-title {
      @include bb-medium-text-styles('md');
      line-height: $bb-spacing-5;
      color: $bb-primary-3;
      cursor: pointer;
      // max-width: 90%;
    }

    span {
      @include bb-medium-text-styles('xs');
      color: $bb-neutral-3;
      letter-spacing: 0.1px;
      font-size: 13px;
    }

    .bullet {
      margin: 0 10px;
    }
  }
}

.inactive-class {
  cursor: not-allowed !important;
}
</style>
